import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { CheckCircle, FormatQuote } from '@mui/icons-material';
import { havenColors } from '../../theme/theme';

// Images
import headerImageGradient from '../../assets/images/header-image-gradient.jpg';
import ecosenseVertScaled from '../../assets/images/ecosense-vert-scaled.webp';
import periodicTableGradient from '../../assets/images/periodic-table-gradient.png';
import monitorAppEcoQube from '../../assets/images/Monitor-App-EcoQube.png';
import accountAssociationGraphic from '../../assets/images/account-association-graphic.png';
import devicePlacementGraphic from '../../assets/images/device-placement-graphic.png';
import dataViewingGraphic from '../../assets/images/data-viewing-graphic.png';
import samanthaPic from '../../assets/images/samantha-pic.png';

// FAQ Icons
import ecosenseIcon from '../../assets/images/Ecosense-icon.png';
import importantIcon from '../../assets/images/important-icon.png';
import appIcon from '../../assets/images/app-icon.png';
import energyIcon from '../../assets/images/Energy-icon.png';
import automationsIconPro from '../../assets/images/Automations-icon-Pro.png';
import radonIcon from '../../assets/images/Radon-icon.png';

// Press Section
import globeAndMailLogo from '../../assets/images/globe-and-mail-logo.png';

const RadonMitigationPage: React.FC = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          background: `linear-gradient(rgba(69, 90, 100, 0.3), rgba(69, 90, 100, 0.3)), url(${headerImageGradient})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" sx={{ fontSize: { xs: '3rem', md: '4.5rem' }, fontWeight: 700, mb: 4 }}>
            The Smart Home Radon Mitigation System
          </Typography>
          <Typography variant="h4" sx={{ mb: 5, opacity: 0.95, maxWidth: '900px', mx: 'auto', fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
            We've partnered with Ecosense to combine their top-of-the-market quality radon monitoring with HAVEN's whole home ventilation solutions.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://haveniaq.com/get-started/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              color: havenColors.primaryTeal,
              fontWeight: 600,
              px: 5,
              py: 2,
              fontSize: '1.2rem',
              '&:hover': {
                background: 'white',
                transform: 'translateY(-2px)',
              }
            }}
          >
            GET STARTED
          </Button>
        </Container>
      </Box>

      {/* Ecosense Partnership Section */}
      <Box sx={{ py: 4, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            {/* Ecosense Logo */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={ecosenseVertScaled} 
                  alt="Ecosense Radon Monitor" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '600px',
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>
            
            {/* Ecosense Content */}
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ 
                mb: 4, 
                fontWeight: 600, 
                color: havenColors.primaryTeal,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}>
                What's Ecosense?
            </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: { xs: '1.2rem', md: '1.3rem' }, 
                lineHeight: 1.7,
                color: '#666',
                mb: 5
              }}>
                Ecosense are the recognized industry leader in radon monitoring, providing results within minutes and the highest accuracy on the market.
                Their EcoQube device is an award-winning radon monitor that captures short and long term trends, which can now be viewed alongside your HAVEN data!
                Over 24,000 deaths a year across North America are caused by radon, and it's the leading cause of lung cancer in non-smokers. 
                That's why monitoring and mitigation are key, and why we were keen to collaborate on a platform which can turn radon data into solutions for healthier homes.
              </Typography>
              <Button
                variant="outlined"
                size="large"
                href="https://ecosense.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: havenColors.primaryTeal,
                  color: havenColors.primaryTeal,
                  fontWeight: 'bold',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: havenColors.primaryTeal,
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                  },
                }}
              >
                MORE ON ECOSENSE
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Better Together Section */}
            <Box
              sx={{
          py: 20,
          background: `url(${periodicTableGradient})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Content */}
            <Grid item xs={12} md={7}>
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' }, 
                fontWeight: 'bold', 
                mb: 5,
                lineHeight: 1.2
              }}>
                Better Together
              </Typography>
              
              <List sx={{ mb: 5 }}>
                <ListItem sx={{ px: 0, alignItems: 'flex-start', mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 45, mt: 0.5 }}>
                    <CheckCircle sx={{ color: 'white', fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="All your air quality insights in one place in the HAVEN IAQ app"
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontSize: { xs: '1.2rem', md: '1.3rem' }, 
                        fontWeight: 500,
                        color: 'white'
                      }
                    }}
                  />
                </ListItem>
                
                <ListItem sx={{ px: 0, alignItems: 'flex-start', mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 45, mt: 0.5 }}>
                    <CheckCircle sx={{ color: 'white', fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Personalized ventilation recommendations, now informed by VOCs and radon"
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontSize: { xs: '1.2rem', md: '1.3rem' }, 
                        fontWeight: 500,
                        color: 'white'
                      }
                    }}
                  />
                </ListItem>
                
                <ListItem sx={{ px: 0, alignItems: 'flex-start', mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 45, mt: 0.5 }}>
                    <CheckCircle sx={{ color: 'white', fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Mitigate radon levels with HAVEN's responsive ventilation controls"
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontSize: { xs: '1.2rem', md: '1.3rem' }, 
                        fontWeight: 500,
                        color: 'white'
                      }
                    }}
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Monitor App Image */}
            <Grid item xs={12} md={5}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={monitorAppEcoQube} 
                  alt="HAVEN Monitor App with EcoQube" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '550px',
                    height: 'auto'
                  }} 
                />
            </Box>
          </Grid>
        </Grid>
      </Container>
      </Box>

      {/* Getting Started Section */}
      <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            color: havenColors.primaryTeal,
            mb: 8,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            Getting Started
          </Typography>
          
          <Grid container spacing={6} sx={{ mb: 8 }}>
            {/* Account Association */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={accountAssociationGraphic} 
                  alt="Account Association" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '250px',
                    height: 'auto',
                    marginBottom: '24px'
                  }} 
                />
                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold',
                  color: havenColors.darkGray,
                  mb: 3,
                  fontSize: { xs: '1.3rem', md: '1.5rem' }
                }}>
                  Account Association
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', md: '1.2rem' }
                }}>
                  Create and connect your Ecosense account with HAVEN.
                </Typography>
              </Box>
            </Grid>

            {/* Device Placement */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={devicePlacementGraphic} 
                  alt="Device Placement" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '250px',
                    height: 'auto',
                    marginBottom: '24px'
                  }} 
                />
                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold',
                  color: havenColors.darkGray,
                  mb: 3,
                  fontSize: { xs: '1.3rem', md: '1.5rem' }
                }}>
                  Device Placement
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', md: '1.2rem' }
                }}>
                  Select the dwelling and zone for your EcoQube device in your HAVEN dashboard.
                </Typography>
              </Box>
            </Grid>

            {/* Data Viewing */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={dataViewingGraphic} 
                  alt="Data Viewing" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '250px',
                    height: 'auto',
                    marginBottom: '24px'
                  }} 
                />
                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold',
                  color: havenColors.darkGray,
                  mb: 3,
                  fontSize: { xs: '1.3rem', md: '1.5rem' }
                }}>
                  Data Viewing
                    </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', md: '1.2rem' }
                }}>
                  Explore your home's Ecosense data alongside your HAVEN ventilation score!
                    </Typography>
              </Box>
              </Grid>
          </Grid>

          {/* Buy Now Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ 
              color: havenColors.darkGray,
              mb: 4,
              fontSize: { xs: '1.3rem', md: '1.5rem' }
            }}>
              Don't have an EcoQube device yet? Get yours today!
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://ecosense.io/en-ca/products/ecoqube"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: havenColors.primaryTeal,
                color: 'white',
                fontWeight: 'bold',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                '&:hover': {
                  backgroundColor: havenColors.darkTeal,
                },
              }}
            >
              BUY NOW
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Press Section */}
      <Box sx={{ 
        py: 12,
        backgroundColor: havenColors.primaryTeal,
        color: 'white'
      }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            mb: 8,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            The Partnership in the Press
        </Typography>
        
          <Grid container spacing={6} alignItems="center">
            {/* Globe and Mail Logo */}
          <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={globeAndMailLogo} 
                  alt="The Globe and Mail" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px',
                    height: 'auto',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '20px',
                    borderRadius: '16px'
                  }} 
                />
              </Box>
          </Grid>
            
            {/* Press Content */}
          <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={{ 
                mb: 4, 
                fontWeight: 500,
                color: 'white',
                fontSize: { xs: '1.3rem', md: '1.5rem' },
                lineHeight: 1.6
              }}>
                "This collaboration marks a significant milestone in the mission to improve indoor air quality, with a focus on preventing radon-induced lung cancer and empowering users with cutting-edge responsive technology..."
              </Typography>
              <Button
                variant="contained"
                size="large"
                href="https://www.theglobeandmail.com/investing/markets/markets-news/GetNews/24726284/ecosense-and-haven-join-forces-to-revolutionize-indoor-air-quality-solutions/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: havenColors.darkGray,
                  color: 'white',
                  fontWeight: 'bold',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  border: '2px solid white',
                  '&:hover': {
                    backgroundColor: 'rgba(69, 90, 100, 0.8)',
                    transform: 'translateY(-2px)',
                    borderColor: 'white',
                  },
                }}
              >
                READ THE FULL ARTICLE
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonial Section */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Quote */}
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                position: 'relative',
                pl: { xs: 2, md: 6 },
                borderLeft: `6px solid ${havenColors.primaryTeal}`
              }}>
                <FormatQuote sx={{ 
                  fontSize: '4rem',
                  color: havenColors.primaryTeal,
                  position: 'absolute',
                  top: -20,
                  right: { xs: -20, md: -30 },
                  opacity: 0.4,
                  transform: 'rotate(180deg)'
                }} />
                <Typography variant="h5" sx={{ 
                  color: havenColors.primaryTeal,
                  fontWeight: 500,
                  lineHeight: 1.6,
                  mb: 4,
                  fontSize: { xs: '1.3rem', md: '1.5rem' }
                }}>
                  Teaming up with HAVEN allows us to synchronize our missions, 
                  empowering individuals and families with an unmatched indoor air 
                  quality monitoring and control system.
              </Typography>
                <Typography variant="h5" sx={{ 
                  color: havenColors.primaryTeal,
                  fontWeight: 500,
                  lineHeight: 1.6,
                  mb: 4,
                  fontSize: { xs: '1.3rem', md: '1.5rem' }
                }}>
                  Together, we have the potential to significantly decrease the 
                  staggering statistic of 24,000 people losing their lives each year to 
                  radon-induced lung cancer in North America. By implementing 
                  effective radon monitoring, we can prevent this cancer and save lives.
              </Typography>
                <Typography variant="body1" sx={{ 
                  color: havenColors.darkGray,
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  - Samantha Kim, Director of Strategic Accounts - <strong>Ecosense</strong>
              </Typography>
              </Box>
            </Grid>

            {/* Photo */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={samanthaPic} 
                  alt="Samantha Kim, Director of Strategic Accounts at Ecosense" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '300px',
                    height: 'auto',
                    borderRadius: '50%',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                  }} 
                />
              </Box>
          </Grid>
        </Grid>
      </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ 
        py: 12,
        background: 'linear-gradient(135deg, #00C4C1 0%, #FFE529 100%)',
        color: 'white'
      }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            mb: 8,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            FAQs
          </Typography>
          
          <Grid container spacing={4}>
            {/* Row 1 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <img 
                    src={ecosenseIcon} 
                    alt="Ecosense Icon" 
                    style={{ 
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  Which Ecosense devices work with HAVEN?
                </Typography>
                <Button
                  variant="outlined"
                  size="medium"
                  href="https://support.haveniaq.com/hc/en-us/articles/27428674544275-Which-Ecosense-devices-work-with-HAVEN"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    mt: 'auto',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  LEARN MORE
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <img 
                    src={importantIcon} 
                    alt="Important Icon" 
                    style={{ 
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  Why is radon mitigation important?
                </Typography>
                <Button
                  variant="outlined"
                  size="medium"
                  href="https://ecosense.io/en-ca/blogs/articles/what-is-radon-gas-this-is-why-you-should-test-your-home?_pos=5&_sid=3db57f764&_ss=r"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    mt: 'auto',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  LEARN MORE
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <img 
                    src={appIcon} 
                    alt="App Icon" 
                    style={{ 
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  If I use HAVEN, do I still need the Ecosense app?
                </Typography>
                <Button
                  variant="outlined"
                  size="medium"
                  href="https://support.haveniaq.com/hc/en-us/articles/27451874449043-If-I-use-HAVEN-do-I-still-need-the-Ecosense-app"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    mt: 'auto',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  LEARN MORE
                </Button>
              </Box>
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <img 
                    src={energyIcon} 
                    alt="Energy Icon" 
                    style={{ 
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  How do I view Ecosense data in the Pro Portal?
                </Typography>
                <Button
                  variant="outlined"
                  size="medium"
                  href="https://support.pro.haveniaq.com/hc/en-us/articles/27452036590867-How-do-I-view-Ecosense-data-in-the-Pro-Portal"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    mt: 'auto',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  LEARN MORE
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <img 
                    src={automationsIconPro} 
                    alt="Automations Icon" 
                    style={{ 
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  How to include radon in automations
          </Typography>
            <Button
                  variant="outlined"
                  size="medium"
                  href="https://support.haveniaq.com/hc/en-us/articles/27428823497619-How-to-include-radon-in-automations"
                  target="_blank"
                  rel="noopener noreferrer"
              sx={{
                    borderColor: 'white',
                    color: 'white',
                    mt: 'auto',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  LEARN MORE
            </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <img 
                    src={radonIcon} 
                    alt="Radon Icon" 
                    style={{ 
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 2,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}>
                  Why not add radon to HAVEN's monitor?
                </Typography>
            <Button
              variant="outlined"
                  size="medium"
                  href="https://support.haveniaq.com/hc/en-us/articles/27452329473043-Why-not-add-radon-to-HAVEN-s-Monitor"
                  target="_blank"
                  rel="noopener noreferrer"
              sx={{
                borderColor: 'white',
                color: 'white',
                    mt: 'auto',
                '&:hover': {
                      borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
              }}
            >
                  LEARN MORE
            </Button>
          </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

    </Box>
  );
};

export default RadonMitigationPage; 